import React, {useEffect, useRef, useState} from 'react';
import {
	Appointment,
	AvailableAppointment,
	AvailableAppointmentsResponse,
	BookAppointmentResponse,
	LEAD_FORM_INTRO,
	SCHEDULER_INTRO,
	Time,
} from '../../models/appointment.model';
import {
	convertAppointments,
	getAppointmentTimesByDate,
	getSchedulerPayload,
	monthYearTitle,
} from '../../services/scheduler.service';
import {
	getFromStore,
	saveToStore,
	StorePath,
	storeSaveAction,
	useStoreActionsFromStore,
} from '../../services/store.service';
import TimeSelector from './Time-selector';
import DateSelector from './Date-selector';
import ConfirmButton from '../common/Confirm-button';
import LeadFormFields from '../forms/Lead-form-fields';
import {Lead} from '../../models/lead.model';
import {STATE} from '../../models/state.model';
import {handleApiResponse, logJsError, bookAppointment, getAvailableAppointments} from '../../services/api.service';
import InfoMessage from './InfoMessage';
import SubmitButton from '../common/Submit-button';
import {GENERIC_ERROR_MESSAGE} from '../../models/system.model';
import {snippetService} from '../../services/snippet.service';

const Scheduler = ({data}: {data: AvailableAppointment[]}) => {
	const storeAction = useStoreActionsFromStore(storeSaveAction);
	const isAgentTyping = getFromStore(StorePath.AGENT_TYPING) as boolean;
	const isSchedulerActiveFromStore = getFromStore(StorePath.IS_SCHEDULER_ACTIVE) as boolean;

	const [allAppointments, setAllAppointments] = useState<AvailableAppointment[][]>([convertAppointments(data)]);
	const [visibleAppointmentsIndex, setVisibleAppointmentsIndex] = useState<number>(0);

	const [allAppointmentsRetrieved, setAllAppointmentsRetrieved] = useState(false);
	const [selectedDate, setSelectedDate] = useState('');
	const [selectedTime, setSelectedTime] = useState<Time | null>(null);
	const [showLeadForm, setShowLeadForm] = useState(false);
	const [submitButtonState, setSubmitButtonState] = useState(STATE.DEFAULT);
	const [showConfirmAppointmentButton, setShowConfirmAppointmentButton] = useState(false);
	const [confirmAppointmentButtonClicked, setConfirmAppointmentButtonClicked] = useState(false);
	const [leadFormData, setLeadFormData] = useState({firstName: '', lastName: '', email: '', phone: ''});
	const [showBookAppointmentResponseMessage, setShowBookAppointmentResponseMessage] = useState(false);
	const [bookAppointmentResponseMessage, setBookAppointmentResponseMessage] = useState('');
	const [errorMessage, setErrorMessage] = useState('');
	const schedulerContainer = useRef<HTMLDivElement | null>(null);
	const availableTimes: Time[] = getAppointmentTimesByDate(selectedDate, allAppointments[visibleAppointmentsIndex]);
	const [isSchedulerDisabled, setIsSchedulerDisabled] = useState(false);

	useEffect(() => {
		if (!isSchedulerDisabled) {
			saveToStore(storeAction, StorePath.IS_SCHEDULER_ACTIVE, true);
		}
	}, []);

	useEffect(() => {
		if (isSchedulerActiveFromStore) {
			setIsSchedulerDisabled(false);
		} else {
			setIsSchedulerDisabled(true);
		}
	}, [isSchedulerActiveFromStore]);

	useEffect(() => {
		if (schedulerContainer.current) {
			schedulerContainer.current.scrollIntoView({block: 'end', behavior: 'smooth'});
		}
		setShowConfirmAppointmentButton(!!(selectedDate && selectedTime?.time));
	}, [availableTimes, selectedDate, selectedTime, showConfirmAppointmentButton, showLeadForm, isAgentTyping, showBookAppointmentResponseMessage, allAppointments, visibleAppointmentsIndex]);

	function resetNonAppointmentRelatedStates() {
		setShowLeadForm(false);
		setShowConfirmAppointmentButton(false);
		setConfirmAppointmentButtonClicked(false);
	}

	const handleDateSelection = (date: string): void => {
		setSelectedDate(date);
		setSelectedTime(null);
		resetNonAppointmentRelatedStates();
	};

	const showEarlierDates = (): void => {
		setVisibleAppointmentsIndex((prevIndex: number) => {
			const newIndex: number = prevIndex - 1;
			return newIndex >= 0 ? newIndex : prevIndex;
		});
		setSelectedDate('');
		setSelectedTime(null);
	};

	const loadMoreDates = (): void => {
		setSelectedTime(null);
		const hasFutureAppointments = visibleAppointmentsIndex < allAppointments.length - 1;
		if (hasFutureAppointments) {
			setVisibleAppointmentsIndex((prevIndex: number) => prevIndex + 1);
			return;
		}

		const currArr = allAppointments[visibleAppointmentsIndex];
		const lastItem = currArr[currArr.length - 1];
		const from = lastItem.date;
		getAvailableAppointments(from)
			.then(handleApiResponse)
			.then((res: AvailableAppointmentsResponse) => {
				if (res.appointments?.length) {
					const newAppointments: AvailableAppointment[] = convertAppointments(res.appointments);
					setVisibleAppointmentsIndex(prevIndex => prevIndex + 1);
					setAllAppointments((prev: AvailableAppointment[][]) => [...prev, newAppointments]);
				} else {
					setAllAppointmentsRetrieved(true);
				}
			});
	};

	const handleTimeSelection = (time: Time): void => {
		setSelectedTime(time);
		resetNonAppointmentRelatedStates();
	};

	const handleConfirmAppointment = (): void => {
		saveToStore(storeAction, StorePath.AGENT_TYPING, true);
		setTimeout(() => {
			setConfirmAppointmentButtonClicked(true);
			saveToStore(storeAction, StorePath.AGENT_TYPING, false);
			setTimeout(() => {
				setShowLeadForm(true);
			}, 1000);
		}, 2000);
	};

	const handleLeadUpdate = (event: React.ChangeEvent<HTMLInputElement>): void => {
		const {name, value} = event.target;
		setLeadFormData({...leadFormData, [name]: value});
	};

	const handleSubmissionResponse = (res: BookAppointmentResponse) => {
		if (res.message) {
			saveToStore(storeAction, StorePath.AGENT_TYPING, true);
			setBookAppointmentResponseMessage(res.message);
			if (res.success) {
				snippetService.sendServiceAppointmentScheduledMessage();
			}
			setTimeout(() => {
				setShowBookAppointmentResponseMessage(true);
				saveToStore(storeAction, StorePath.AGENT_TYPING, false);
			}, 3000);
		} else {
			setErrorMessage(GENERIC_ERROR_MESSAGE);
		}
	};

	const handleSubmitLead = (event: React.FormEvent<HTMLFormElement>): void => {
		event.preventDefault();
		setSubmitButtonState(STATE.PROCESSING);
		const payload: {lead: Lead, appointment: Appointment} = getSchedulerPayload(leadFormData, selectedDate, selectedTime as Time);
		bookAppointment(payload)
			.then(handleApiResponse)
			.then((res: BookAppointmentResponse) => {
				setSubmitButtonState(STATE.COMPLETED);
				handleSubmissionResponse(res);
				saveToStore(storeAction, StorePath.IS_SCHEDULER_ACTIVE, false);
			})
			.catch((error) => {
				logJsError(error);
				setSubmitButtonState(STATE.COMPLETED);
				setErrorMessage(GENERIC_ERROR_MESSAGE);
				saveToStore(storeAction, StorePath.IS_SCHEDULER_ACTIVE, false);
			});
	};

	const disabledClasses = submitButtonState === STATE.COMPLETED || isSchedulerDisabled
		? ' pointer-events-none opacity-40'
		: '';

	return (
		<div ref={schedulerContainer} className={'mt-8 pb-8'}>
			{!!allAppointments[visibleAppointmentsIndex]?.length &&
				<div className="select-none">
					<div className={'mb-10'}>
						<InfoMessage text={SCHEDULER_INTRO} />
					</div>
					<div className={'flex flex-row place-content-between'}>
						<h2 className="font-semibold mb-4">{monthYearTitle(allAppointments[visibleAppointmentsIndex])}</h2>
					</div>
					<div className={disabledClasses}>
						<DateSelector
							appointments={allAppointments[visibleAppointmentsIndex]}
							selectedDate={selectedDate}
							onSelectDate={handleDateSelection}
							loadMoreDates={loadMoreDates}
							loadEarlierDates={showEarlierDates}
							showLoadMoreDatesBtn={!allAppointmentsRetrieved}
							showEarlierDatesBtn={visibleAppointmentsIndex > 0}
							hasLaterDatesLoaded={visibleAppointmentsIndex < allAppointments.length - 1}
						/>
						{!!availableTimes?.length &&
							<TimeSelector
								availableTimes={availableTimes as Time[]}
								selectedTime={selectedTime as Time}
								onSelectTime={handleTimeSelection}
							/>
						}
						<ConfirmButton
							show={showConfirmAppointmentButton}
							disabled={confirmAppointmentButtonClicked}
							text={'Confirm Appointment'}
							onclick={handleConfirmAppointment}
						/>
					</div>
				</div>
			}
			{confirmAppointmentButtonClicked &&
				<>
					<InfoMessage text={LEAD_FORM_INTRO}/>
					{showLeadForm &&
						<div className={disabledClasses}>
							<form onSubmit={handleSubmitLead}>
								<LeadFormFields formData={leadFormData} onChange={handleLeadUpdate} isPhoneRequired={true}/>
								<SubmitButton state={submitButtonState}/>
							</form>
						</div>
					}
				</>
			}

			{submitButtonState === STATE.COMPLETED && showBookAppointmentResponseMessage && <InfoMessage html={bookAppointmentResponseMessage}/>}
			{errorMessage && <InfoMessage text={errorMessage}/>}
		</div>
	);
};

export default Scheduler;
